import React, { useState, useEffect } from 'react';
import { Card, Typography, Box, Chip, Stack, Grid, Paper, CircularProgress, Alert, Button, Pagination } from '@mui/material';
import { styled } from '@mui/material/styles';
import { Icon } from '@iconify/react';
import environment from '@iconify/icons-ant-design/environment';
import pushpin from '@iconify/icons-ant-design/pushpin';
import thunderbolt from '@iconify/icons-ant-design/thunderbolt';
import clockCircle from '@iconify/icons-ant-design/clock-circle';
import team from '@iconify/icons-ant-design/team';
import refresh from '@iconify/icons-ant-design/reload';
import { useLiveChargingStatus } from 'src/hooks/useLiveChargingStatus.hook';

// ----------------------------------------------------------------------

const StyledCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(3),
  borderRadius: theme.shape.borderRadiusMd,
  boxShadow: theme.customShadows.z4,
  border: `1px solid ${theme.palette.secondary.light}40`,
  background: `linear-gradient(135deg, ${theme.palette.secondary.lighter}08 0%, ${theme.palette.secondary.light}05 100%)`,
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
}));

const StatusChip = styled(Chip)(({ theme, status }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'online':
        return {
          color: theme.palette.success.main,
          backgroundColor: theme.palette.success.lighter,
        };
      case 'charging':
        return {
          color: theme.palette.info.main,
          backgroundColor: theme.palette.info.lighter,
        };
      case 'offline':
        return {
          color: theme.palette.error.main,
          backgroundColor: theme.palette.error.lighter,
        };
      case 'maintenance':
        return {
          color: theme.palette.warning.main,
          backgroundColor: theme.palette.warning.lighter,
        };
      default:
        return {
          color: theme.palette.grey[600],
          backgroundColor: theme.palette.grey[100],
        };
    }
  };

  const colors = getStatusColor();
  
  return {
    color: colors.color,
    backgroundColor: colors.backgroundColor,
    fontWeight: 600,
    fontSize: '0.75rem',
    height: 24,
    '& .MuiChip-label': {
      padding: '0 8px',
    },
  };
});

const StationCard = styled(Paper)(({ theme }) => ({
  padding: theme.spacing(2),
  borderRadius: theme.shape.borderRadius,
  border: `1px solid ${theme.palette.grey[200]}`,
  cursor: 'pointer'
}));

const MetricCard = styled(Box)(({ theme, status }) => {
  const getStatusColor = () => {
    switch (status) {
      case 'online':
        return {
          main: theme.palette.success.main,
          light: theme.palette.success.light,
          lighter: theme.palette.success.lighter,
        };
      case 'charging':
        return {
          main: theme.palette.info.main,
          light: theme.palette.info.light,
          lighter: theme.palette.info.lighter,
        };
      case 'offline':
        return {
          main: theme.palette.error.main,
          light: theme.palette.error.light,
          lighter: theme.palette.error.lighter,
        };
      case 'maintenance':
        return {
          main: theme.palette.warning.main,
          light: theme.palette.warning.light,
          lighter: theme.palette.warning.lighter,
        };
      default:
        return {
          main: theme.palette.grey[600],
          light: theme.palette.grey[400],
          lighter: theme.palette.grey[100],
        };
    }
  };

  const colors = getStatusColor();

  return {
    padding: theme.spacing(2.5),
    borderRadius: theme.shape.borderRadius * 1.5,
    background: `linear-gradient(135deg, ${theme.palette.background.paper} 0%, ${colors.lighter}15 50%, ${colors.light}08 100%)`,
    border: `1px solid ${colors.light}30`,
    textAlign: 'center',
    height: '100%',
    minHeight: '100px',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: `0 8px 25px ${colors.light}20`,
      border: `1px solid ${colors.light}50`,
    },
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: 0,
      right: 0,
      height: '3px',
      background: `linear-gradient(90deg, ${colors.main} 0%, ${colors.light} 100%)`,
      borderRadius: `${theme.shape.borderRadius * 1.5}px ${theme.shape.borderRadius * 1.5}px 0 0`,
    },
  };
});

// Add CSS keyframes for spin animation
const spinKeyframes = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

// Inject the keyframes into the document head
if (typeof document !== 'undefined' && !document.getElementById('refresh-spin-keyframes')) {
  const style = document.createElement('style');
  style.id = 'refresh-spin-keyframes';
  style.textContent = spinKeyframes;
  document.head.appendChild(style);
}

const LiveChargingStatusMap = () => {
  const {
    data,
    isLoading,
    error,
    selectedStation,
    setSelectedStation,
    refresh
  } = useLiveChargingStatus();

  const { summary, stations } = data || {};

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const stationsPerPage = 3;

  // Sort stations by active status (online and charging first, then offline and maintenance)
  const sortedStations = React.useMemo(() => {
    if (!stations || stations.length === 0) return [];
    
    return [...stations].sort((a, b) => {
      // Define priority order: online > charging > offline > maintenance
      const getStatusPriority = (status) => {
        switch (status) {
          case 'online': return 1;
          case 'charging': return 2;
          case 'offline': return 3;
          case 'maintenance': return 4;
          default: return 5;
        }
      };
      
      const aPriority = getStatusPriority(a.status);
      const bPriority = getStatusPriority(b.status);
      
      // If same priority, sort by name alphabetically
      if (aPriority === bPriority) {
        return (a.name || a.chargepointId || '').localeCompare(b.name || b.chargepointId || '');
      }
      
      return aPriority - bPriority;
    });
  }, [stations]);

  // Calculate pagination
  const totalPages = Math.ceil((sortedStations?.length || 0) / stationsPerPage);
  const startIndex = (currentPage - 1) * stationsPerPage;
  const endIndex = startIndex + stationsPerPage;
  const currentStations = sortedStations?.slice(startIndex, endIndex) || [];

  // Reset to first page when stations data changes
  useEffect(() => {
    setCurrentPage(1);
  }, [stations]);

  // Handle page change
  const handlePageChange = (event, page) => {
    setCurrentPage(page);
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'online':
        return <Icon icon={thunderbolt} width={16} height={16} />;
      case 'charging':
        return <Icon icon={clockCircle} width={16} height={16} />;
      case 'offline':
        return <Icon icon={pushpin} width={16} height={16} />;
      case 'maintenance':
        return <Icon icon={team} width={16} height={16} />;
      default:
        return <Icon icon={pushpin} width={16} height={16} />;
    }
  };

  // Format last update time
  const formatLastUpdate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    const now = new Date();
    const updateTime = new Date(timestamp);
    const diffMinutes = Math.floor((now - updateTime) / (1000 * 60));
    
    if (diffMinutes < 1) return 'Just now';
    if (diffMinutes < 60) return `${diffMinutes} min ago`;
    const diffHours = Math.floor(diffMinutes / 60);
    if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
    const diffDays = Math.floor(diffHours / 24);
    return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
  };

  // Handle loading state
  if (isLoading) {
    return (
      <StyledCard>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 400 }}>
          <CircularProgress />
        </Box>
      </StyledCard>
    );
  }

  // Handle error state
  if (error) {
    return (
      <StyledCard>
        <Alert 
          severity="error" 
          action={
            <Button color="inherit" size="small" onClick={refresh}>
              <Icon icon={refresh} width={16} height={16} style={{ marginRight: 4 }} />
              Retry
            </Button>
          }
        >
          Failed to load live charging status: {error.message}
        </Alert>
      </StyledCard>
    );
  }

  return (
    <StyledCard>
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h6" gutterBottom sx={{ fontWeight: 600, color: 'text.primary' }}>
            Charging Station - Live Status
          </Typography>
        </Box>
        <Box
          onClick={isLoading ? undefined : refresh}
          sx={{
            cursor: isLoading ? 'not-allowed' : 'pointer',
            opacity: isLoading ? 0.6 : 1,
            color: 'black',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: 1,
            borderRadius: '50%',
            transition: 'all 0.2s ease-in-out',
            '&:hover': {
              color: 'grey.800',
              transform: 'scale(1.1)',
              backgroundColor: 'grey.100'
            },
            '&:active': {
              transform: 'scale(0.95)'
            }
          }}
        >
          <Icon 
            icon={refresh} 
            width={24} 
            height={24}
            style={{
              animation: isLoading ? 'spin 1s linear infinite' : 'none'
            }}
          />
        </Box>
      </Box>

      {/* Status Overview */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={6} sm={3}>
          <MetricCard status="online">
            <Typography variant="h4" sx={{ color: 'success.main', fontWeight: 700, mb: 0.5 }}>
              {summary?.online || 0}
            </Typography>
            <Typography variant="body2" sx={{ color: 'success.dark', fontWeight: 500 }}>
              Online
            </Typography>
          </MetricCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricCard status="charging">
            <Typography variant="h4" sx={{ color: 'info.main', fontWeight: 700, mb: 0.5 }}>
              {summary?.charging || 0}
            </Typography>
            <Typography variant="body2" sx={{ color: 'info.dark', fontWeight: 500 }}>
              Charging
            </Typography>
          </MetricCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricCard status="offline">
            <Typography variant="h4" sx={{ color: 'error.main', fontWeight: 700, mb: 0.5 }}>
              {summary?.offline || 0}
            </Typography>
            <Typography variant="body2" sx={{ color: 'error.dark', fontWeight: 500 }}>
              Offline
            </Typography>
          </MetricCard>
        </Grid>
        <Grid item xs={6} sm={3}>
          <MetricCard status="maintenance">
            <Typography variant="h4" sx={{ color: 'warning.main', fontWeight: 700, mb: 0.5 }}>
              {summary?.maintenance || 0}
            </Typography>
            <Typography variant="body2" sx={{ color: 'warning.dark', fontWeight: 500 }}>
              Maintenance
            </Typography>
          </MetricCard>
        </Grid>
      </Grid>

      {/* Station List */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Station Details
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Showing {startIndex + 1}-{Math.min(endIndex, sortedStations?.length || 0)} of {sortedStations?.length || 0} stations
          </Typography>
        </Box>
        
        <Stack spacing={2}>
          {currentStations.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                No charging stations found
              </Typography>
            </Box>
          ) : (
            currentStations.map((station) => (
              <StationCard
                key={station.id}
                onClick={() => setSelectedStation(station)}
                sx={{
                  borderColor: selectedStation?.id === station.id ? 'primary.main' : 'grey.200',
                  bgcolor: selectedStation?.id === station.id ? 'primary.lighter' : 'background.paper',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                    {station.name}
                  </Typography>
                  <StatusChip
                    status={station.status}
                    label={station.status.toUpperCase()}
                    icon={getStatusIcon(station.status)}
                  />
                </Box>
                
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 1 }}>
                  {station.location?.address || station.chargepointId}
                </Typography>
                
                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                      <Icon icon={team} width={14} height={14} />
                      <Typography variant="body2">
                        {station.connectors?.active || 0}/{station.connectors?.total || 0} active
                      </Typography>
                    </Box>
                  </Grid>
                  <Grid item xs={6}>
                    <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                      {station.powerOutput ? parseFloat(station.powerOutput).toFixed(2) + ' kW' : 'N/A'}
                    </Typography>
                  </Grid>
                </Grid>
                
                <Typography variant="caption" sx={{ color: 'text.secondary', mt: 1, display: 'block' }}>
                  Last updated: {formatLastUpdate(station.lastUpdate)}
                </Typography>
              </StationCard>
            ))
          )}
        </Stack>

        {/* Pagination Controls */}
        {totalPages > 1 && (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
            <Pagination
              count={totalPages}
              page={currentPage}
              onChange={handlePageChange}
              color="primary"
              size="medium"
              showFirstButton
              showLastButton
              sx={{
                '& .MuiPaginationItem-root': {
                  fontWeight: 600,
                },
              }}
            />
          </Box>
        )}
      </Box>
    </StyledCard>
  );
};

export default LiveChargingStatusMap;
